import { supabase } from '../lib/supabase';

export const demoDataService = {
  async resetDemoData() {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Only allow admins to reset demo data
    const { data: profile } = await supabase
      .from('profiles')
      .select('user_type')
      .eq('id', user.id)
      .single();

    if (!profile || (profile.user_type !== 'admin' && profile.user_type !== 'moderator')) {
      throw new Error('No tienes permisos para resetear datos demo');
    }

    try {
      // Delete in order to respect foreign key constraints
      console.log('Deleting demo data...');

      // 1. Delete notifications
      await supabase.from('notifications').delete().neq('id', '00000000-0000-0000-0000-000000000000');
      
      // 2. Delete story views
      await supabase.from('story_views').delete().neq('id', '00000000-0000-0000-0000-000000000000');
      
      // 3. Delete stories
      await supabase.from('stories').delete().neq('id', '00000000-0000-0000-0000-000000000000');
      
      // 4. Delete event attendees
      await supabase.from('event_attendees').delete().neq('id', '00000000-0000-0000-0000-000000000000');
      
      // 5. Delete events
      await supabase.from('events').delete().neq('id', '00000000-0000-0000-0000-000000000000');
      
      // 6. Delete collaboration applications
      await supabase.from('collaboration_applications').delete().neq('id', '00000000-0000-0000-0000-000000000000');
      
      // 7. Delete collaborations
      await supabase.from('collaborations').delete().neq('id', '00000000-0000-0000-0000-000000000000');
      
      // 8. Delete reports
      await supabase.from('reports').delete().neq('id', '00000000-0000-0000-0000-000000000000');
      
      // 9. Delete follows
      await supabase.from('follows').delete().neq('id', '00000000-0000-0000-0000-000000000000');
      
      // 10. Delete orders/sales/transactions
      await supabase.from('invoices').delete().neq('id', '00000000-0000-0000-0000-000000000000');
      await supabase.from('sales').delete().neq('id', '00000000-0000-0000-0000-000000000000');
      await supabase.from('orders').delete().neq('id', '00000000-0000-0000-0000-000000000000');
      await supabase.from('transactions').delete().neq('id', '00000000-0000-0000-0000-000000000000');
      
      // 11. Delete payment data
      await supabase.from('payment_methods').delete().neq('id', '00000000-0000-0000-0000-000000000000');
      await supabase.from('payment_settings').delete().neq('id', '00000000-0000-0000-0000-000000000000');
      await supabase.from('payouts').delete().neq('id', '00000000-0000-0000-0000-000000000000');
      
      // 12. Delete cart and wishlist
      await supabase.from('cart_items').delete().neq('id', '00000000-0000-0000-0000-000000000000');
      await supabase.from('wishlist_items').delete().neq('id', '00000000-0000-0000-0000-000000000000');
      
      // 13. Delete collection posts
      await supabase.from('collection_posts').delete().neq('id', '00000000-0000-0000-0000-000000000000');
      
      // 14. Delete collections
      await supabase.from('collections').delete().neq('id', '00000000-0000-0000-0000-000000000000');
      
      // 15. Delete analytics
      await supabase.from('user_analytics').delete().neq('id', '00000000-0000-0000-0000-000000000000');
      
      // 16. Delete messages and conversations
      await supabase.from('messages').delete().neq('id', '00000000-0000-0000-0000-000000000000');
      await supabase.from('conversations').delete().neq('id', '00000000-0000-0000-0000-000000000000');
      
      // 17. Delete likes
      await supabase.from('likes').delete().neq('id', '00000000-0000-0000-0000-000000000000');
      
      // 18. Delete comments
      await supabase.from('comments').delete().neq('id', '00000000-0000-0000-0000-000000000000');
      
      // 19. Delete media files
      await supabase.from('media_files').delete().neq('id', '00000000-0000-0000-0000-000000000000');
      
      // 20. Delete posts
      await supabase.from('posts').delete().neq('id', '00000000-0000-0000-0000-000000000000');
      
      // 21. Delete profiles (except current user and other real users)
      // Only delete profiles that were created as demo data
      await supabase.from('profiles').delete()
        .in('username', [
          'maria_painter', 'carlos_music', 'ana_photo', 'visitor_juan', 
          'luis_design', 'sara_sculpture', 'moderator_admin'
        ]);

      console.log('Demo data reset completed successfully');
      return true;
    } catch (error) {
      console.error('Error resetting demo data:', error);
      throw error;
    }
  },

  async createDemoData() {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Only allow admins to create demo data
    const { data: profile } = await supabase
      .from('profiles')
      .select('user_type')
      .eq('id', user.id)
      .single();

    if (!profile || (profile.user_type !== 'admin' && profile.user_type !== 'moderator')) {
      throw new Error('No tienes permisos para crear datos demo');
    }

    try {
      console.log('Creating demo data...');

      // Create demo profiles
      const demoProfiles = [
        {
          id: '11111111-1111-1111-1111-111111111111',
          username: 'maria_painter',
          full_name: 'María González',
          birth_date: '1995-03-15',
          user_type: 'artist',
          is_premium: false,
          bio: 'Pintora abstracta especializada en técnicas mixtas y expresionismo moderno.',
          art_categories: ['painting', 'mixed_media'],
          location: 'Madrid, España',
          avatar_url: 'https://images.pexels.com/photos/1181519/pexels-photo-1181519.jpeg?auto=compress&cs=tinysrgb&w=100&h=100&fit=crop',
        },
        {
          id: '22222222-2222-2222-2222-222222222222',
          username: 'carlos_music',
          full_name: 'Carlos Rodríguez',
          birth_date: '1992-07-22',
          user_type: 'artist',
          is_premium: true,
          bio: 'Compositor y productor musical especializado en música electrónica y ambient.',
          art_categories: ['music', 'sound_design'],
          location: 'Barcelona, España',
          avatar_url: 'https://images.pexels.com/photos/1526814/pexels-photo-1526814.jpeg?auto=compress&cs=tinysrgb&w=100&h=100&fit=crop',
        },
        {
          id: '33333333-3333-3333-3333-333333333333',
          username: 'ana_photo',
          full_name: 'Ana Martínez',
          birth_date: '1998-11-08',
          user_type: 'artist',
          is_premium: false,
          bio: 'Fotógrafa de retratos y naturaleza. Me especializo en capturar emociones auténticas.',
          art_categories: ['photography', 'portrait'],
          location: 'Valencia, España',
          avatar_url: 'https://images.pexels.com/photos/1239291/pexels-photo-1239291.jpeg?auto=compress&cs=tinysrgb&w=100&h=100&fit=crop',
        },
        {
          id: '44444444-4444-4444-4444-444444444444',
          username: 'visitor_juan',
          full_name: 'Juan Pérez',
          birth_date: '1990-05-30',
          user_type: 'visitor',
          is_premium: false,
          bio: 'Amante del arte en todas sus formas. Coleccionista y crítico amateur.',
          art_categories: null,
          location: 'Sevilla, España',
          avatar_url: 'https://images.pexels.com/photos/1222271/pexels-photo-1222271.jpeg?auto=compress&cs=tinysrgb&w=100&h=100&fit=crop',
        },
      ];

      // Insert demo profiles
      const { error: profilesError } = await supabase
        .from('profiles')
        .upsert(demoProfiles);

      if (profilesError) throw profilesError;

      // Create demo posts
      const demoPosts = [
        {
          id: 'aaaaaaaa-aaaa-aaaa-aaaa-aaaaaaaaaaaa',
          author_id: '11111111-1111-1111-1111-111111111111',
          title: 'Sinfonía de Colores',
          description: 'Una exploración abstracta de las emociones a través del color y la textura. Esta pieza representa el caos y la armonía que coexisten en nuestras vidas diarias.',
          category: 'painting',
          tags: ['abstracto', 'colorido', 'moderno', 'emocional'],
          for_sale: true,
          price: 450.00,
          likes_count: 23,
          comments_count: 8,
          views_count: 156,
        },
        {
          id: 'bbbbbbbb-bbbb-bbbb-bbbb-bbbbbbbbbbbb',
          author_id: '22222222-2222-2222-2222-222222222222',
          title: 'Ambient Nocturno',
          description: 'Track ambient de 8 minutos creado completamente con sintetizadores analógicos. Perfecto para meditación y relajación profunda.',
          category: 'music',
          tags: ['ambient', 'electronico', 'relajante', 'sintetizador'],
          for_sale: true,
          price: 25.00,
          likes_count: 45,
          comments_count: 12,
          views_count: 289,
        },
        {
          id: 'cccccccc-cccc-cccc-cccc-cccccccccccc',
          author_id: '33333333-3333-3333-3333-333333333333',
          title: 'Retrato en Luz Natural',
          description: 'Serie de retratos capturados durante la hora dorada. La luz natural realza la belleza y personalidad únicas de cada sujeto.',
          category: 'photography',
          tags: ['retrato', 'luz_natural', 'hora_dorada', 'profesional'],
          for_sale: false,
          price: null,
          likes_count: 67,
          comments_count: 15,
          views_count: 312,
        },
      ];

      const { error: postsError } = await supabase
        .from('posts')
        .upsert(demoPosts);

      if (postsError) throw postsError;

      // Create demo media files
      const demoMediaFiles = [
        {
          post_id: 'aaaaaaaa-aaaa-aaaa-aaaa-aaaaaaaaaaaa',
          type: 'image',
          url: 'https://images.pexels.com/photos/1183992/pexels-photo-1183992.jpeg?auto=compress&cs=tinysrgb&w=800',
          file_name: 'sinfonia_colores.jpg',
          file_size: 1024000,
          width: 800,
          height: 600,
        },
        {
          post_id: 'bbbbbbbb-bbbb-bbbb-bbbb-bbbbbbbbbbbb',
          type: 'audio',
          url: 'https://www.soundjay.com/misc/sounds/sound_effect_boing.mp3',
          file_name: 'ambient_nocturno.mp3',
          file_size: 8192000,
          duration: 480,
        },
        {
          post_id: 'cccccccc-cccc-cccc-cccc-cccccccccccc',
          type: 'image',
          url: 'https://images.pexels.com/photos/1239291/pexels-photo-1239291.jpeg?auto=compress&cs=tinysrgb&w=800',
          file_name: 'retrato_luz_natural.jpg',
          file_size: 2048000,
          width: 800,
          height: 1200,
        },
      ];

      const { error: mediaError } = await supabase
        .from('media_files')
        .upsert(demoMediaFiles);

      if (mediaError) throw mediaError;

      // Create some demo interactions
      const demoLikes = [
        { user_id: '44444444-4444-4444-4444-444444444444', post_id: 'aaaaaaaa-aaaa-aaaa-aaaa-aaaaaaaaaaaa' },
        { user_id: '44444444-4444-4444-4444-444444444444', post_id: 'bbbbbbbb-bbbb-bbbb-bbbb-bbbbbbbbbbbb' },
        { user_id: '33333333-3333-3333-3333-333333333333', post_id: 'aaaaaaaa-aaaa-aaaa-aaaa-aaaaaaaaaaaa' },
      ];

      const { error: likesError } = await supabase
        .from('likes')
        .upsert(demoLikes);

      if (likesError) throw likesError;

      // Create demo follows
      const demoFollows = [
        { follower_id: '44444444-4444-4444-4444-444444444444', following_id: '11111111-1111-1111-1111-111111111111' },
        { follower_id: '44444444-4444-4444-4444-444444444444', following_id: '22222222-2222-2222-2222-222222222222' },
        { follower_id: '33333333-3333-3333-3333-333333333333', following_id: '11111111-1111-1111-1111-111111111111' },
      ];

      const { error: followsError } = await supabase
        .from('follows')
        .upsert(demoFollows);

      if (followsError) throw followsError;

      console.log('Demo data created successfully');
      return true;
    } catch (error) {
      console.error('Error creating demo data:', error);
      throw error;
    }
  },

  async getDemoDataStats() {
    try {
      const [
        { count: profilesCount },
        { count: postsCount },
        { count: likesCount },
        { count: commentsCount },
        { count: followsCount },
        { count: notificationsCount }
      ] = await Promise.all([
        supabase.from('profiles').select('*', { count: 'exact', head: true }),
        supabase.from('posts').select('*', { count: 'exact', head: true }),
        supabase.from('likes').select('*', { count: 'exact', head: true }),
        supabase.from('comments').select('*', { count: 'exact', head: true }),
        supabase.from('follows').select('*', { count: 'exact', head: true }),
        supabase.from('notifications').select('*', { count: 'exact', head: true }),
      ]);

      return {
        profiles: profilesCount || 0,
        posts: postsCount || 0,
        likes: likesCount || 0,
        comments: commentsCount || 0,
        follows: followsCount || 0,
        notifications: notificationsCount || 0,
      };
    } catch (error) {
      console.error('Error getting demo data stats:', error);
      return {
        profiles: 0,
        posts: 0,
        likes: 0,
        comments: 0,
        follows: 0,
        notifications: 0,
      };
    }
  },
};